import { msalInstance } from '../index'

// Helper: best‑effort removal of all non-HttpOnly cookies for current and parent domains.
// HttpOnly cookies (set by server with HttpOnly flag) cannot be cleared via client JS; server must expire them.
function clearAllCookies(preserve = []) {
  try {
    const cookieString = document.cookie
    if (!cookieString) return

    const rawCookies = cookieString.split(';')
    const names = rawCookies
      .map((c) => c.split('=')[0].trim())
      .filter((n) => n && !preserve.includes(n))
    if (!names.length) return

    // Build domain permutations to cover subdomain + root. e.g. a.b.example.com -> .a.b.example.com, .b.example.com, .example.com
    const hostParts = window.location.hostname.split('.')
    const domainVariants = []
    for (let i = 0; i < hostParts.length - 1; i += 1) {
      const fragment = hostParts.slice(i).join('.')
      domainVariants.push(`.${fragment}`)
    }
    // Also include the exact host (covers localhost or single-part hosts)
    domainVariants.push(window.location.hostname)

    names.forEach((name) => {
      // Clear for each candidate domain + root path
      domainVariants.forEach((d) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${d}`
      })
      // Fallback without domain attribute
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('Cookie clearing issue', err)
  }
}

// Performs a robust logout:
// 1. Clear application storage (local + session keys we set)
// 2. Clear accessible cookies (MSAL / App Insights / custom) – best effort, not HttpOnly
// 3. Clear MSAL browser storage entries (handled by msalInstance + logout redirect)
// 4. Add a short-lived flag to block back-button cache restore
// 5. Redirect to Azure AD end_session endpoint
// Optional: pass { preserveCookies: ['cookieName'] } to retain specific cookies.
export const appLogout = async (options = {}) => {
  const { preserveCookies = [] } = options
  try {
    // Clear custom app storage
    const storageKeys = ['accessToken', 'userSessionName', 'userSessionEmail', 'lastActivity']
    storageKeys.forEach((k) => {
      sessionStorage.removeItem(k)
      localStorage.removeItem(k)
    })

    // Best-effort cookie clearing
    clearAllCookies(preserveCookies)

    // Clear MSAL active account (logoutRedirect will fully sign out and clear relevant cache entries)
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length) {
      // No explicit manual removal required in MSAL 3.x for accounts; kept for informational clarity.
    }

    // Mark logout time to help components avoid using stale cached React state
    sessionStorage.setItem('postLogout', Date.now().toString())

    // HashRouter note: postLogoutRedirectUri '/' returns to the login (root) view.
    // Removed onRedirectNavigate override because returning false blocks the redirect to AAD end_session.
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: '/',
    })
    // Safety fallback: if for some reason redirect didn't occur (popup blockers etc.), hard navigate.
    setTimeout(() => {
      if (!msalInstance.getActiveAccount()) {
        // Already cleared; ensure we're on root.
        if (window.location.hash !== '' || window.location.pathname !== '/') {
          window.location.href = '/'
        }
      }
    }, 4000)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Logout error', e)
    // Fallback to hard navigation if something went wrong
    window.location.href = '/'
  }
}
